import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { useFearGreedChart } from "@/hooks/coinglass/useFearGreedChart";
import { LoadingSpinner } from "@/components/common";

interface FearGreedGaugeProps {
  height?: string;
}

export const FearGreedGauge: React.FC<FearGreedGaugeProps> = ({
  height = "300px",
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  // We only need the latest data point for the gauge
  const { data, isLoading, isError, error } = useFearGreedChart({ limit: 1 });

  // Handle resize (window and container)
  useEffect(() => {
    const handleResize = () => {
      chartInstanceRef.current?.resize();
    };

    window.addEventListener("resize", handleResize);
    
    // Also observe the container for size changes
    const observer = new ResizeObserver(handleResize);
    if (chartRef.current) {
      observer.observe(chartRef.current);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      observer.disconnect();
    };
  }, []);

  // Initialize and update chart
  useEffect(() => {
    if (!chartRef.current || isLoading || !data) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    // Get the latest value
    // Assuming data.series[0] is the Fear & Greed values
    const latestValue = data.series[0]?.data[data.series[0].data.length - 1] || 0;
    
    // Determine color based on value
    let color = "#eab308"; // Default yellow
    let statusText = "Neutral";
    
    if (latestValue <= 40) {
      color = "#22c55e"; // Green
      statusText = "Fear";
    } else if (latestValue <= 60) {
      color = "#eab308"; // Yellow
      statusText = "Neutral";
    } else {
      color = "#ef4444"; // Red
      statusText = "Greed";
    }

    const option: echarts.EChartsOption = {
      backgroundColor: "transparent",
      series: [
        {
          type: "gauge",
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max: 100,
          splitNumber: 5,
          radius: "90%",
          center: ["50%", "65%"],
          itemStyle: {
            color: color,
          },
          progress: {
            show: false,
          },
          pointer: {
            icon: "triangle",
            length: "15%",
            width: 12,
            offsetCenter: [0, "-35%"],
            itemStyle: {
              color: "auto",
            },
          },
          axisLine: {
            roundCap: true,
            lineStyle: {
              width: 15, // Skinny line
              color: [
                [0.4, "#22c55e"], // 0-40 Fear (Green)
                [0.6, "#eab308"], // 40-60 Neutral (Yellow)
                [1, "#ef4444"],   // 60-100 Greed (Red)
              ],
            },
          },
          axisTick: {
            length: 4,
            distance: -20, // Move inside
            lineStyle: {
              color: "#fff",
              width: 1,
              opacity: 0.5,
            },
          },
          splitLine: {
            length: 8,
            distance: -20, // Move inside
            lineStyle: {
              color: "#fff",
              width: 2,
              opacity: 0.8,
            },
          },
          axisLabel: {
            color: "#9ca3af",
            fontSize: 10,
            distance: -35,
            formatter: function (value) {
              if (value === 0 || value === 100) return "";
              return value + "";
            },
          },
          title: {
            offsetCenter: [0, "25%"],
            fontSize: 16,
            color: "#fff",
          },
          detail: {
            fontSize: 32,
            offsetCenter: [0, "-5%"],
            valueAnimation: true,
            formatter: function (value) {
              return Math.round(value) + "";
            },
            color: "auto",
          },
          data: [
            {
              value: latestValue,
              name: statusText,
            },
          ],
        },
      ],
    };

    chartInstanceRef.current.setOption(option);
  }, [data, isLoading]);

  if (isError) {
    return (
      <div
        className="flex items-center justify-center w-full bg-red-500/10 border border-red-500/20 rounded-lg"
        style={{ height }}>
        <div className="text-center p-4">
          <h3 className="text-red-400 font-medium mb-2">Error</h3>
          <p className="text-red-300/80 text-sm">
            {(error as Error)?.message || "Failed to load data"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full" style={{ height }}>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50 z-10 backdrop-blur-sm rounded-lg">
          <LoadingSpinner />
        </div>
      )}
      <div ref={chartRef} className="w-full h-full" />
    </div>
  );
};
